import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Mail, Loader2, RefreshCw, Send, CheckCircle, XCircle, Eye, FileText, Newspaper,
  Plus, Trash2, GripVertical, EyeOff, Image as ImageIcon, Upload, X, Star, Users, AlertTriangle,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTO_BLOCKS = [
  ['this_week', 'This Week in the Water'],
  ['upcoming_trips', 'Upcoming Trips'],
  ['courses_spotlight', 'ISC Training Spotlight'],
  ['special_offers', 'Special Offers'],
  ['community', 'Community Highlights'],
];

const STATUS_PILLS = {
  generating: { label: 'Generating…', bg: 'bg-amber-100', fg: 'text-amber-700' },
  pending_approval: { label: 'Awaiting approval', bg: 'bg-blue-100', fg: 'text-blue-700' },
  approved: { label: 'Approved', bg: 'bg-emerald-100', fg: 'text-emerald-700' },
  rejected: { label: 'Rejected', bg: 'bg-red-100', fg: 'text-red-700' },
  sending: { label: 'Sending…', bg: 'bg-amber-100', fg: 'text-amber-700' },
  sent: { label: 'Sent', bg: 'bg-emerald-100', fg: 'text-emerald-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', fg: 'text-gray-700' },
  failed: { label: 'Failed', bg: 'bg-red-100', fg: 'text-red-700' },
};

// Friendly labels for the standard generated section keys
const KEY_LABELS = {
  weekly_splash: 'Weekly Splash',
  top_story: 'Top Story',
  world_dive_radar: 'World Dive Radar',
  mozambique_reef: 'Mozambique Reef Watch',
  south_african_reef: 'SA Reef Report',
  creature_feature: 'Creature Feature',
  ocean_discovery: 'Ocean Discovery',
  wrong_doing: "What You're Doing Wrong",
  instructor_secret: 'Instructor Secret',
  ask_instructor: 'Ask An Instructor',
  dive_bucket_list: 'Dive Bucket List',
  gear_we_use: 'Gear We Actually Use',
};

const labelFor = (s) => {
  if (!s) return 'Section';
  if (s.key && KEY_LABELS[s.key]) return KEY_LABELS[s.key];
  return s.title || 'Custom section';
};

// ===== Media Library Picker =====
const MediaPicker = ({ token, open, onClose, onPick }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterTag, setFilterTag] = useState('');
  const fileInputRef = useRef(null);
  const [pendingTags, setPendingTags] = useState('');
  const [pendingTitle, setPendingTitle] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/newsletter-media`, { headers });
      setItems(r.data || []);
    } catch (e) { toast.error('Failed to load media'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  // Client-side resize/compress before upload. Caps at ~1600px wide and JPEG
  // quality 0.85 so giant phone/DSLR photos (10-50MB) become ~300-700KB and
  // safely make it through Cloudflare's 100s edge timeout on production.
  const compressImage = (file) => new Promise((resolve) => {
    if (!file.type.startsWith('image/') || file.type === 'image/webp') {
      resolve(file); return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1600;
        let { width, height } = img;
        if (width > MAX_W) {
          height = Math.round((height * MAX_W) / width);
          width = MAX_W;
        }
        // Skip if already small
        if (file.size < 800 * 1024 && width === img.width) {
          resolve(file); return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          const ext = file.type === 'image/png' ? 'png' : 'jpg';
          const name = file.name.replace(/\.[^.]+$/, '') + `.${ext}`;
          resolve(new File([blob], name, { type: blob.type }));
        }, file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85);
      };
      img.onerror = () => resolve(file);
      img.src = ev.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

  const uploadOne = async (file) => {
    const compressed = await compressImage(file);
    const fd = new FormData();
    fd.append('file', compressed);
    const up = await axios.post(`${API_URL}/admin/upload`, fd, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
    const url = up.data.image_url;
    const tags = pendingTags.split(',').map((t) => t.trim()).filter(Boolean);
    const title = pendingTitle
      ? `${pendingTitle} — ${file.name.replace(/\.[^.]+$/, '')}`
      : file.name.replace(/\.[^.]+$/, '');
    await axios.post(`${API_URL}/admin/newsletter-media`, { url, title, tags }, { headers });
    return { ok: true, name: file.name };
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0; let failed = 0;
    for (const f of files) {
      try {
        await uploadOne(f);
        ok += 1;
      } catch (err) {
        failed += 1;
        const msg = err.response?.data?.detail || err.message || 'Upload failed';
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setPendingTags(''); setPendingTitle('');
    if (ok) toast.success(`${ok} image${ok > 1 ? 's' : ''} uploaded${failed ? ` (${failed} failed)` : ''}`);
    await reload();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMedia = async (id) => {
    if (!window.confirm('Delete this image from the library?')) return;
    try {
      await axios.delete(`${API_URL}/admin/newsletter-media/${id}`, { headers });
      await reload();
    } catch (e) { toast.error('Delete failed'); }
  };

  const updateTags = async (id, tagsStr) => {
    const tags = tagsStr.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      await axios.patch(`${API_URL}/admin/newsletter-media/${id}`, { tags }, { headers });
      await reload();
    } catch (e) { toast.error('Tag update failed'); }
  };

  const setDefaultHero = async (m) => {
    try {
      await axios.patch(
        `${API_URL}/admin/newsletter-media/${m.id}`,
        { is_default_hero: !m.is_default_hero },
        { headers },
      );
      toast.success(m.is_default_hero ? 'Default hero unset' : 'Set as default hero — replaces Unsplash fallbacks');
      await reload();
    } catch (e) { toast.error('Update failed'); }
  };

  const visible = items.filter((m) => {
    if (!filterTag.trim()) return true;
    const q = filterTag.trim().toLowerCase();
    return (m.tags || []).some((t) => t.includes(q))
      || (m.title || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#FF6B35]" /> Newsletter Media Library
          </DialogTitle>
        </DialogHeader>

        <div className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="text-xs font-bold uppercase text-[#0A2342]">Upload new photo</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input placeholder="Title (optional)" value={pendingTitle} onChange={(e) => setPendingTitle(e.target.value)} data-testid="media-upload-title" />
            <Input placeholder="Tags (comma-separated, e.g. mozambique, turtle)" value={pendingTags} onChange={(e) => setPendingTags(e.target.value)} data-testid="media-upload-tags" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
              data-testid="media-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Uploading…' : 'Upload images'}
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
          </div>
          <p className="text-[11px] text-gray-500">Pick one or many — large photos are auto-resized to 1600px / ~85% quality in your browser before upload (keeps things fast on a flaky connection). Tagged photos auto-attach to matching sections when the renderer can&apos;t find an article image. Use tags like <em>mozambique, sodwana, turtle, shark, training, gear</em>.</p>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <Input
            placeholder="Filter by tag or title"
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="max-w-xs"
            data-testid="media-filter-input"
          />
          <span className="text-xs text-gray-500">{visible.length} of {items.length}</span>
        </div>

        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" /></div>
        ) : visible.length === 0 ? (
          <div className="text-center text-gray-500 py-10 text-sm">No images yet. Upload one above.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
            {visible.map((m) => (
              <div key={m.id} className={`border rounded-md overflow-hidden bg-white flex flex-col ${m.is_default_hero ? 'ring-2 ring-[#FF6B35]' : ''}`} data-testid={`media-item-${m.id}`}>
                <div className="relative">
                  <button onClick={() => onPick(m)} className="aspect-video bg-slate-100 overflow-hidden block hover:opacity-90 w-full" data-testid={`media-pick-${m.id}`}>
                    <img src={m.url} alt={m.title} className="w-full h-full object-cover" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDefaultHero(m); }}
                    className={`absolute top-1 right-1 rounded-full p-1.5 shadow ${m.is_default_hero ? 'bg-[#FF6B35] text-white' : 'bg-white/90 text-gray-500 hover:text-[#FF6B35]'}`}
                    title={m.is_default_hero ? 'This is the default hero. Click to unset.' : 'Set as default hero (replaces Unsplash fallback)'}
                    data-testid={`media-hero-${m.id}`}
                  >
                    <Star className={`w-4 h-4 ${m.is_default_hero ? 'fill-current' : ''}`} />
                  </button>
                  {m.is_default_hero && (
                    <div className="absolute bottom-1 left-1 text-[10px] bg-[#FF6B35] text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Default hero</div>
                  )}
                </div>
                <div className="p-2 text-xs space-y-1">
                  <div className="font-medium text-[#0A2342] truncate" title={m.title}>{m.title || '—'}</div>
                  <div className="flex flex-wrap gap-1">
                    {(m.tags || []).map((t) => (
                      <span key={t} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{t}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      defaultValue={(m.tags || []).join(', ')}
                      onBlur={(e) => updateTags(m.id, e.target.value)}
                      placeholder="edit tags"
                      className="text-[11px] h-7"
                    />
                    <button onClick={() => removeMedia(m.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== Single Section Editor Card =====
const SectionCard = ({ section, index, onPatch, onDelete, onOpenMedia, dragHandleProps }) => {
  const s = section;
  return (
    <div className={`border rounded-md p-3 bg-white ${s.hidden ? 'opacity-60' : ''}`} data-testid={`nl-section-card-${s.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span {...dragHandleProps} className="cursor-grab text-gray-400 hover:text-gray-600 shrink-0" data-testid={`nl-section-drag-${s.id}`}>
            <GripVertical className="w-4 h-4" />
          </span>
          <span className="text-xs font-bold uppercase text-[#FF6B35] truncate">{labelFor(s)}</span>
          {s.custom && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Custom</span>}
          {s.hidden && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Hidden</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onPatch(s.id, { hidden: !s.hidden })}
            className="p-1.5 text-gray-500 hover:text-[#0A2342]"
            title={s.hidden ? 'Show section' : 'Hide section'}
            data-testid={`nl-section-hide-${s.id}`}
          >
            {s.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete(s.id)}
            className="p-1.5 text-gray-500 hover:text-red-600"
            title="Delete section"
            data-testid={`nl-section-delete-${s.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <Input
        value={s.title || ''}
        onChange={(e) => onPatch(s.id, { title: e.target.value })}
        placeholder="Section title"
        className="mb-2 text-sm"
        data-testid={`nl-section-title-${s.id}`}
      />
      <textarea
        value={s.body || ''}
        onChange={(e) => onPatch(s.id, { body: e.target.value })}
        rows={5}
        className="w-full text-sm border rounded p-2 font-mono"
        placeholder="Inline HTML body"
        data-testid={`nl-section-body-${s.id}`}
      />
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Input
          value={s.cta_label || ''}
          onChange={(e) => onPatch(s.id, { cta_label: e.target.value })}
          placeholder="CTA label"
          className="text-xs"
          data-testid={`nl-section-cta-label-${s.id}`}
        />
        <Input
          value={s.cta_url || ''}
          onChange={(e) => onPatch(s.id, { cta_url: e.target.value })}
          placeholder="CTA URL"
          className="text-xs"
          data-testid={`nl-section-cta-url-${s.id}`}
        />
      </div>

      <div className="mt-2 flex items-center gap-2">
        {s.image_url ? (
          <div className="relative">
            <img src={s.image_url} alt="" className="w-20 h-14 object-cover rounded border" />
            <button
              onClick={() => onPatch(s.id, { image_url: null, image_credit: null })}
              className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5 shadow text-red-500 hover:bg-red-50"
              title="Remove image"
              data-testid={`nl-section-img-clear-${s.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-14 bg-slate-100 rounded border flex items-center justify-center text-gray-400">
            <ImageIcon className="w-4 h-4" />
          </div>
        )}
        <div className="flex-1 space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenMedia(s.id)}
            className="text-xs"
            data-testid={`nl-section-img-pick-${s.id}`}
          >
            <ImageIcon className="w-3.5 h-3.5 mr-1" /> Pick from Media Library
          </Button>
          <Input
            value={s.image_credit || ''}
            onChange={(e) => onPatch(s.id, { image_credit: e.target.value })}
            placeholder="Image credit (optional)"
            className="text-[11px] h-7"
          />
        </div>
      </div>
    </div>
  );
};

// ===== Main Tab =====
const NewslettersTab = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [editForm, setEditForm] = useState({ subject_line: '', preview_text: '', sections: [], community_html: '', hidden_auto_blocks: [], hero_image: '' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [sources, setSources] = useState([]);
  const [newSource, setNewSource] = useState({ name: '', url: '', tier: 2 });
  const [mediaOpen, setMediaOpen] = useState(false);
  // When picking media specifically FOR a section, this holds that section id.
  const [pickingForSection, setPickingForSection] = useState(null);
  const [recipients, setRecipients] = useState(null); // { total_unique_recipients, members:{...}, standalone_subscribers:{...} }

  const loadRecipients = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/newsletters/recipients/preview`, { headers });
      setRecipients(r.data);
    } catch (e) { /* silent */ }
  }, [headers]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/newsletters`, { headers });
      setItems(r.data);
    } catch (e) {
      toast.error('Failed to load newsletters');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-poll while any newsletter is `generating` OR `sending`. This has two effects:
  //   1) UI updates the status pill in real time (generating → pending_approval / failed,
  //      sending → sent)
  //   2) The recurring HTTP requests keep the K8s pod "hot" so the
  //      asyncio background task running the LLM call / send dispatch doesn't get
  //      killed by the platform's idle scale-down. Without this, every
  //      newsletter generation or send would end as "failed: server restarted".
  useEffect(() => {
    const isBusy = items.some((n) => n.status === 'generating' || n.status === 'sending');
    if (!isBusy) return;
    const iv = setInterval(() => { reload(); }, 15000);
    return () => clearInterval(iv);
  }, [items, reload]);

  const normaliseSections = (sections) => {
    if (Array.isArray(sections)) return sections.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return [];
  };

  const openDetail = async (id) => {
    setOpenId(id);
    setOpenDoc(null);
    setDirty(false);
    setRecipientsPreview(null);
    try {
      const r = await axios.get(`${API_URL}/admin/newsletters/${id}`, { headers });
      setOpenDoc(r.data);
      setEditForm({
        subject_line: r.data.subject_line || '',
        preview_text: r.data.preview_text || '',
        sections: normaliseSections(r.data.sections),
        community_html: r.data.community_html || '',
        hidden_auto_blocks: Array.isArray(r.data.hidden_auto_blocks) ? r.data.hidden_auto_blocks : [],
        hero_image: r.data.hero_image || '',
      });
    } catch (e) {
      toast.error('Failed to load newsletter');
    }
  };

  // If the currently-open modal is in `generating` state, silently re-fetch
  // it every 10s so the user sees the moment it flips to pending_approval
  // (and so the frontend keeps the pod hot with recurring requests).
  useEffect(() => {
    if (!openId || openDoc?.status !== 'generating') return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/admin/newsletters/${openId}`, { headers });
        setOpenDoc(r.data);
        if (r.data.status !== 'generating') {
          setEditForm({
            subject_line: r.data.subject_line || '',
            preview_text: r.data.preview_text || '',
            sections: normaliseSections(r.data.sections),
            community_html: r.data.community_html || '',
            hidden_auto_blocks: Array.isArray(r.data.hidden_auto_blocks) ? r.data.hidden_auto_blocks : [],
            hero_image: r.data.hero_image || '',
          });
        }
      } catch (e) { /* silent */ }
    }, 10000);
    return () => clearInterval(iv);
  }, [openId, openDoc?.status, headers]);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/admin/newsletters/generate`, {}, { headers });
      toast.success('Generation started — Claude is writing your magazine. Refresh in ~30s.');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    } finally { setGenerating(false); }
  };

  // ----- Full save (subject/preview/community + bulk sections) -----
  const saveAll = async () => {
    if (!openDoc) return;
    setSaving(true);
    try {
      // Build sections payload as id-keyed map for the legacy PUT endpoint.
      const sectionsPayload = {};
      editForm.sections.forEach((s) => {
        sectionsPayload[s.id || s.key] = {
          title: s.title || '',
          body: s.body || '',
          cta_label: s.cta_label || null,
          cta_url: s.cta_url || null,
          image_url: s.image_url || null,
          image_credit: s.image_credit || null,
        };
      });
      const body = {
        subject_line: editForm.subject_line,
        preview_text: editForm.preview_text,
        community_html: editForm.community_html,
        sections: sectionsPayload,
        hidden_auto_blocks: editForm.hidden_auto_blocks || [],
      };
      const r = await axios.put(`${API_URL}/admin/newsletters/${openDoc.id}`, body, { headers });
      setOpenDoc(r.data);
      setEditForm((prev) => ({ ...prev, sections: normaliseSections(r.data.sections) }));
      setDirty(false);
      toast.success('Saved');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // ----- Section-level immediate operations (uses dedicated CRUD endpoints
  // so reorders/deletes/visibility don't depend on the full save round-trip) -----
  const patchSectionLocal = (id, patch) => {
    setEditForm((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
    setDirty(true);
  };

  const persistSectionPatch = async (id, patch) => {
    try {
      const r = await axios.patch(`${API_URL}/admin/newsletters/${openDoc.id}/sections/${id}`, patch, { headers });
      setOpenDoc(r.data);
      setEditForm((prev) => ({ ...prev, sections: normaliseSections(r.data.sections) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const toggleHidden = async (id) => {
    const s = editForm.sections.find((x) => x.id === id);
    if (!s) return;
    patchSectionLocal(id, { hidden: !s.hidden });
    await persistSectionPatch(id, { hidden: !s.hidden });
  };

  const onSectionImage = async (id, image_url, image_credit) => {
    patchSectionLocal(id, { image_url, image_credit });
    await persistSectionPatch(id, { image_url, image_credit });
  };

  const onSectionPatch = (id, patch) => {
    // 'hidden' is special — persist immediately so HTML re-renders.
    if ('hidden' in patch) { toggleHidden(id); return; }
    // 'image_url' (clear) is also persisted immediately.
    if ('image_url' in patch && patch.image_url === null) {
      onSectionImage(id, null, null); return;
    }
    patchSectionLocal(id, patch);
  };

  const addSection = async () => {
    if (!openDoc) return;
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/${openDoc.id}/sections`, {
        title: 'New section', body: '',
      }, { headers });
      setOpenDoc(r.data);
      setEditForm((prev) => ({ ...prev, sections: normaliseSections(r.data.sections) }));
      toast.success('Section added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add section');
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this section? You can also just hide it.')) return;
    try {
      const r = await axios.delete(`${API_URL}/admin/newsletters/${openDoc.id}/sections/${id}`, { headers });
      setOpenDoc(r.data);
      setEditForm((prev) => ({ ...prev, sections: normaliseSections(r.data.sections) }));
      toast.success('Section removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const reordered = editForm.sections.slice();
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setEditForm((prev) => ({ ...prev, sections: reordered }));
    try {
      const r = await axios.put(`${API_URL}/admin/newsletters/${openDoc.id}/sections/reorder`, {
        order: reordered.map((s) => s.id),
      }, { headers });
      setOpenDoc(r.data);
      setEditForm((prev) => ({ ...prev, sections: normaliseSections(r.data.sections) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reorder failed');
    }
  };

  // ----- Media picker open helpers -----
  const openMediaForSection = (sectionId) => {
    setPickingForSection(sectionId);
    setMediaOpen(true);
  };

  const onMediaPicked = async (media) => {
    if (pickingForSection === '__hero__') {
      // Hero image pick — update the form + persist immediately so the live
      // preview refreshes without waiting for a full Save.
      setEditForm((prev) => ({ ...prev, hero_image: media.url }));
      setDirty(true);
      setPickingForSection(null);
      setMediaOpen(false);
      try {
        await axios.put(`${API_URL}/admin/newsletters/${openDoc.id}`, { hero_image: media.url }, { headers });
        await openDetail(openDoc.id);
        toast.success('Opening image updated');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to set opening image');
      }
      return;
    }
    if (pickingForSection) {
      const credit = media.title ? `© Just Dive · ${media.title}` : '© Just Dive';
      await onSectionImage(pickingForSection, media.url, credit);
      setPickingForSection(null);
      setMediaOpen(false);
      toast.success('Image attached');
    } else {
      // Library opened standalone — just close.
      setMediaOpen(false);
    }
  };

  const clearHeroImage = async () => {
    setEditForm((prev) => ({ ...prev, hero_image: '' }));
    setDirty(true);
    try {
      await axios.put(`${API_URL}/admin/newsletters/${openDoc.id}`, { hero_image: '' }, { headers });
      await openDetail(openDoc.id);
      toast.success('Reverted to default opening image');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear opening image');
    }
  };

  // ----- Approval actions -----
  const doAction = async (action) => {
    if (!openDoc) return;
    const confirmMsgs = {
      send: `Send this newsletter to all opted-in subscribers? This cannot be undone.`,
      reject: 'Reject this newsletter?',
    };
    if (confirmMsgs[action] && !window.confirm(confirmMsgs[action])) return;
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/${openDoc.id}/${action}`, {}, { headers });
      toast.success(action === 'send' ? `Queued for ${r.data.queued} recipients` : `Marked ${action}`);
      await openDetail(openDoc.id);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const previewEmail = async () => {
    if (!openDoc?.html) {
      toast.error("This draft has no content yet — click 'Generate new' to create a fresh draft first.");
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/newsletters/${openDoc.id}/preview-email`, {}, { headers });
      toast.success('Preview email sent to admin recipients');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send preview email'); }
  };

  // ----- Content sources -----
  const loadSources = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/content-sources`, { headers });
      setSources(r.data);
    } catch (e) { /* silent */ }
  }, [headers]);

  useEffect(() => { if (showSources) loadSources(); }, [showSources, loadSources]);

  const addSource = async () => {
    if (!newSource.url) { toast.error('URL required'); return; }
    try {
      await axios.post(`${API_URL}/admin/content-sources`, { ...newSource, kind: 'rss', active: true }, { headers });
      setNewSource({ name: '', url: '', tier: 2 });
      await loadSources();
      toast.success('Source added');
    } catch (e) { toast.error('Failed'); }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Delete this source?')) return;
    try { await axios.delete(`${API_URL}/admin/content-sources/${id}`, { headers }); await loadSources(); }
    catch (e) { toast.error('Failed'); }
  };

  // ----- Delete a draft / failed / rejected newsletter -----
  const deleteDraft = async (n) => {
    const label = n.subject_line || '(untitled draft)';
    if (!window.confirm(`Delete "${label}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/newsletters/${n.id}`, { headers });
      toast.success('Draft deleted');
      if (openId === n.id) { setOpenId(null); setOpenDoc(null); }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ----- Force-fail a stuck 'generating' draft -----
  const cancelGenerating = async (n) => {
    if (!window.confirm('Cancel this generation? The task appears to have stalled. You can then click "Generate new draft" to retry.')) return;
    try {
      await axios.post(`${API_URL}/admin/newsletters/${n.id}/cancel`, {}, { headers });
      toast.success('Generation cancelled');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const resumeSend = async (n) => {
    if (!window.confirm(`Resume sending "${n.subject_line || 'this draft'}"? We'll skip anyone who already received it and continue with the rest.`)) return;
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/${n.id}/resume-send`, {}, { headers });
      toast.success(`Resuming — ${r.data.queued} remaining`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resume');
    }
  };

  const cancelActiveSend = async (n) => {
    if (!window.confirm(`Stop this send now?\n\nRecipients already delivered (${n.sent_count || 0}) will keep their copy. The remaining ${(n.recipient_total || 0) - (n.sent_count || 0)} won't receive it.`)) return;
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/${n.id}/cancel-send`, {}, { headers });
      toast.success(`Send cancelled — ${r.data.sent}/${r.data.total} delivered`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel send');
    }
  };

  // ----- Recipients preview + prune -----
  const [recipientsPreview, setRecipientsPreview] = useState(null);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [pruning, setPruning] = useState(false);

  const loadRecipientsPreview = async () => {
    if (!openDoc?.id) return;
    setLoadingRecipients(true);
    try {
      const r = await axios.get(`${API_URL}/admin/newsletters/${openDoc.id}/recipients-preview`, { headers });
      setRecipientsPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load recipients');
    } finally { setLoadingRecipients(false); }
  };

  const pruneInvalidRecipients = async () => {
    if (!recipientsPreview?.invalid_list?.length) return;
    const emails = recipientsPreview.invalid_list.map((r) => r.email);
    if (!window.confirm(`Remove ${emails.length} invalid address(es) from your newsletter list? They'll be opted out permanently.`)) return;
    setPruning(true);
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/prune-recipients`, { invalid: emails }, { headers });
      toast.success(`Cleaned up ${r.data.invalid_removed} address(es)`);
      await loadRecipientsPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to prune');
    } finally { setPruning(false); }
  };

  const pruneBouncedRecipients = async () => {
    if (!recipientsPreview?.bounced_list?.length) return;
    const emails = recipientsPreview.bounced_list.map((r) => r.email);
    if (!window.confirm(`Permanently suppress ${emails.length} previously-bounced address(es)? They already skip future sends but this makes it explicit.`)) return;
    setPruning(true);
    try {
      const r = await axios.post(`${API_URL}/admin/newsletters/prune-recipients`, { bounced: emails }, { headers });
      toast.success(`Suppressed ${r.data.bounced_suppressed} address(es)`);
      await loadRecipientsPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to suppress');
    } finally { setPruning(false); }
  };

  // ===== Render =====
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-[#FF6B35]" /> Weekly Ocean Report
          </h2>
          <p className="text-sm text-gray-500">Generate, review, approve and send the Just Dive weekly newsletter. Drafts are auto-created every Monday at 06:00 SAST.</p>
          {recipients && (
            <p className="text-xs mt-1 text-[#0A2342]" data-testid="nl-recipient-count">
              <span className="font-bold text-[#FF6B35]">{recipients.total_unique_recipients}</span> recipients ready ·
              <span className="ml-1">{recipients.members.opted_in}/{recipients.members.total} members opted in</span>
              {recipients.members.opted_out > 0 && (
                <span className="ml-1 text-gray-500">({recipients.members.opted_out} unsubscribed)</span>
              )}
              {recipients.standalone_subscribers.unique > 0 && (
                <span className="ml-1">+ {recipients.standalone_subscribers.unique} standalone</span>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reload} data-testid="nl-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => { setPickingForSection(null); setMediaOpen(true); }} data-testid="nl-media-library-btn">
            <ImageIcon className="w-4 h-4 mr-2" /> Media library
          </Button>
          <Button variant="outline" onClick={() => setShowSources(true)} data-testid="nl-content-sources-btn">Content sources</Button>
          <Button onClick={generate} disabled={generating} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="nl-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Generate new draft
          </Button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Mail className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p>No newsletters yet. Click <strong>Generate new draft</strong> to create your first issue.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Recipients</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((n) => {
                const pill = STATUS_PILLS[n.status] || STATUS_PILLS.failed;
                return (
                  <tr key={n.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 max-w-md">
                      <div className="font-medium text-[#0A2342] truncate">{n.subject_line || <span className="text-gray-400">(no subject yet)</span>}</div>
                      <div className="text-xs text-gray-500 truncate">{n.preview_text}</div>
                      {n.status === 'failed' && n.error && (
                        <div className="text-xs text-red-600 mt-1 truncate" title={n.error} data-testid={`nl-error-${n.id}`}>
                          Error: {n.error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><span className={`text-xs ${pill.bg} ${pill.fg} px-2 py-0.5 rounded`}>{pill.label}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{n.recipients_count != null ? `${n.sent_count || 0}/${n.recipients_count}` : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openDetail(n.id)} data-testid={`nl-open-${n.id}`}>Review</Button>
                        {n.status === 'generating' && (
                          <Button size="sm" variant="outline" onClick={() => cancelGenerating(n)} className="text-amber-700 border-amber-300" data-testid={`nl-cancel-${n.id}`}>Cancel</Button>
                        )}
                        {n.status === 'sending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => resumeSend(n)} className="text-blue-700 border-blue-300" data-testid={`nl-resume-${n.id}`}>Resume</Button>
                            <Button size="sm" variant="outline" onClick={() => cancelActiveSend(n)} className="text-red-700 border-red-300" data-testid={`nl-cancel-send-${n.id}`}>Stop send</Button>
                          </>
                        )}
                        {n.status !== 'sent' && n.status !== 'sending' && (
                          <button
                            onClick={() => deleteDraft(n)}
                            className="p-1.5 text-gray-400 hover:text-red-600"
                            title="Delete this draft"
                            data-testid={`nl-delete-${n.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail / edit / approve dialog */}
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setOpenDoc(null); } }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#FF6B35]" /> Newsletter Review</DialogTitle>
          </DialogHeader>
          {!openDoc ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                {openDoc.status === 'failed' && (
                  <div className="border border-red-200 bg-red-50 text-red-700 rounded-md p-3 text-sm" data-testid="nl-failure-banner">
                    <div className="font-semibold mb-1">Generation failed</div>
                    <div className="text-xs whitespace-pre-wrap break-words">
                      {openDoc.error || 'No error details captured. Click "Generate new" to retry.'}
                    </div>
                  </div>
                )}
                {openDoc.status === 'generating' && (
                  <div className="border border-amber-200 bg-amber-50 text-amber-700 rounded-md p-3 text-sm" data-testid="nl-generating-banner">
                    <div className="font-semibold mb-1">Still generating…</div>
                    <div className="text-xs">Claude usually takes 60-120 seconds. Close this and refresh the list in a moment.</div>
                  </div>
                )}
                {openDoc.status !== 'failed' && openDoc.status !== 'generating' && (!openDoc.html || (editForm.sections || []).length === 0) && (
                  <div className="border border-orange-200 bg-orange-50 text-orange-800 rounded-md p-3 text-sm" data-testid="nl-empty-banner">
                    <div className="font-semibold mb-1">Empty draft — nothing to edit or preview</div>
                    <div className="text-xs">
                      This draft has no sections and no HTML body (an older bug saved an empty result). Close this dialog, delete this row, then click <strong>Generate new draft</strong> from the list to create a fresh one.
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Subject line</label>
                  <Input
                    value={editForm.subject_line}
                    onChange={(e) => { setEditForm({ ...editForm, subject_line: e.target.value }); setDirty(true); }}
                    className="font-medium"
                    data-testid="nl-edit-subject"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Inbox preview text</label>
                  <Input
                    value={editForm.preview_text}
                    onChange={(e) => { setEditForm({ ...editForm, preview_text: e.target.value }); setDirty(true); }}
                    placeholder="~80 chars — what people see next to the subject"
                    data-testid="nl-edit-preview"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Opening image</label>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="w-40 h-20 rounded-md overflow-hidden border bg-gray-100 flex-shrink-0" data-testid="nl-hero-thumb">
                      <img
                        src={editForm.hero_image || 'https://images.unsplash.com/photo-1530053969600-caed2596d242?w=640&q=80&auto=format'}
                        alt="Opening"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setPickingForSection('__hero__'); setMediaOpen(true); }}
                        data-testid="nl-hero-pick"
                      >
                        <ImageIcon className="w-3.5 h-3.5 mr-1" />
                        {editForm.hero_image ? 'Change opening image' : 'Pick opening image'}
                      </Button>
                      {editForm.hero_image && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-500 hover:text-red-600 justify-start"
                          onClick={clearHeroImage}
                          data-testid="nl-hero-clear"
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> Revert to default
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t pt-3">
                  <div className="text-xs font-bold uppercase text-[#0A2342]">Sections ({editForm.sections.length})</div>
                  <Button size="sm" variant="outline" onClick={addSection} data-testid="nl-add-section">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add section
                  </Button>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="nl-sections">
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        className="space-y-2"
                        data-testid="nl-sections-list"
                      >
                        {editForm.sections.map((s, idx) => (
                          <Draggable key={s.id || `s-${idx}`} draggableId={s.id || `s-${idx}`} index={idx}>
                            {(prov) => (
                              <div ref={prov.innerRef} {...prov.draggableProps}>
                                <SectionCard
                                  section={s}
                                  index={idx}
                                  onPatch={onSectionPatch}
                                  onDelete={deleteSection}
                                  onOpenMedia={openMediaForSection}
                                  dragHandleProps={prov.dragHandleProps}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <div className="border rounded-md p-3 bg-slate-50">
                  <div className="text-xs font-bold uppercase text-[#0A2342] mb-2">Auto-built blocks</div>
                  <p className="text-[11px] text-gray-500 mb-2">These are auto-rendered from the live storefront (only future trips/events). Untick any you don&apos;t want in this issue.</p>
                  <div className="space-y-1.5">
                    {AUTO_BLOCKS.map(([key, label]) => {
                      const hidden = (editForm.hidden_auto_blocks || []).includes(key);
                      return (
                        <label key={key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`nl-autoblock-${key}`}>
                          <input
                            type="checkbox"
                            checked={!hidden}
                            onChange={() => {
                              const cur = new Set(editForm.hidden_auto_blocks || []);
                              if (hidden) cur.delete(key); else cur.add(key);
                              setEditForm({ ...editForm, hidden_auto_blocks: Array.from(cur) });
                              setDirty(true);
                            }}
                            className="rounded"
                          />
                          <span className={hidden ? 'text-gray-400 line-through' : 'text-[#0A2342]'}>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="border rounded-md p-3 bg-slate-50">
                  <div className="text-xs font-bold uppercase text-[#FF6B35] mb-2">Community Highlights (optional)</div>
                  <textarea
                    value={editForm.community_html}
                    onChange={(e) => { setEditForm({ ...editForm, community_html: e.target.value }); setDirty(true); }}
                    rows={4}
                    className="w-full text-sm border rounded p-2 font-mono"
                    placeholder="Inline HTML — student certs, club activities, anything you'd like to celebrate."
                    data-testid="nl-community-html"
                  />
                </div>

                <div className="flex flex-wrap gap-2 pt-3 sticky bottom-0 bg-white py-3 border-t">
                  <Button onClick={saveAll} disabled={saving || !dirty} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="nl-save-edits">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {dirty ? 'Save edits' : 'Saved'}
                  </Button>
                  <Button variant="outline" onClick={previewEmail} data-testid="nl-email-preview">
                    <Mail className="w-4 h-4 mr-2" /> Email preview to admins
                  </Button>
                  <Button
                    variant="outline"
                    onClick={loadRecipientsPreview}
                    disabled={loadingRecipients}
                    data-testid="nl-preview-recipients"
                  >
                    {loadingRecipients ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                    Preview recipients
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF6F1]"
                    onClick={async () => {
                      if (!window.confirm('Generate a brand-new draft? Your current edits will stay saved on this issue — the new one will appear in the list separately.')) return;
                      setOpenId(null);
                      setOpenDoc(null);
                      await generate();
                    }}
                    disabled={generating}
                    data-testid="nl-generate-fresh"
                  >
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Generate new
                  </Button>
                  {openDoc.status !== 'sent' && openDoc.status !== 'sending' && (
                    <>
                      <Button variant="outline" className="text-emerald-700 border-emerald-200" onClick={() => doAction('approve')} disabled={openDoc.status === 'approved'} data-testid="nl-approve">
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button variant="outline" className="text-red-600" onClick={() => doAction('reject')} disabled={openDoc.status === 'rejected'} data-testid="nl-reject">
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button onClick={() => doAction('send')} disabled={openDoc.status !== 'approved'} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="nl-send">
                        <Send className="w-4 h-4 mr-2" /> Send to subscribers
                      </Button>
                    </>
                  )}
                </div>

                {recipientsPreview && (
                  <div className="border rounded-md p-4 bg-slate-50 space-y-3" data-testid="nl-recipients-panel">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold uppercase text-[#0A2342]">Recipients preview</div>
                      <button onClick={() => setRecipientsPreview(null)} className="text-gray-400 hover:text-gray-700" data-testid="nl-recipients-close">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-emerald-700" data-testid="nl-recipients-will-send">{recipientsPreview.will_send}</div>
                        <div className="text-[10px] uppercase tracking-wide text-emerald-800">Will receive</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-red-700" data-testid="nl-recipients-invalid">{recipientsPreview.invalid_domain}</div>
                        <div className="text-[10px] uppercase tracking-wide text-red-800">Invalid domain</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-amber-700" data-testid="nl-recipients-bounced">{recipientsPreview.suppressed_bounced}</div>
                        <div className="text-[10px] uppercase tracking-wide text-amber-800">Bounced (skipped)</div>
                      </div>
                      <div className="bg-gray-100 border border-gray-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-gray-600" data-testid="nl-recipients-optout">{recipientsPreview.opted_out}</div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-700">Opted out</div>
                      </div>
                    </div>

                    {recipientsPreview.invalid_list?.length > 0 && (
                      <div className="border border-red-200 rounded bg-white">
                        <div className="flex items-center justify-between px-3 py-2 border-b bg-red-50">
                          <div className="text-xs font-semibold text-red-800 flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Invalid addresses ({recipientsPreview.invalid_list.length})
                          </div>
                          <Button size="sm" variant="outline" className="text-red-700 border-red-300 h-7 text-xs" onClick={pruneInvalidRecipients} disabled={pruning} data-testid="nl-prune-invalid">
                            <Trash2 className="w-3 h-3 mr-1" /> Remove all
                          </Button>
                        </div>
                        <div className="max-h-40 overflow-y-auto text-xs divide-y">
                          {recipientsPreview.invalid_list.map((r, i) => (
                            <div key={i} className="px-3 py-1.5 flex items-center justify-between">
                              <span className="font-mono text-gray-800 truncate mr-2">{r.email}</span>
                              <span className="text-gray-500 text-[11px] whitespace-nowrap">{r.reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {recipientsPreview.bounced_list?.length > 0 && (
                      <div className="border border-amber-200 rounded bg-white">
                        <div className="flex items-center justify-between px-3 py-2 border-b bg-amber-50">
                          <div className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Previously bounced ({recipientsPreview.bounced_list.length})
                          </div>
                          <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 h-7 text-xs" onClick={pruneBouncedRecipients} disabled={pruning} data-testid="nl-prune-bounced">
                            Keep suppressed
                          </Button>
                        </div>
                        <div className="max-h-40 overflow-y-auto text-xs divide-y">
                          {recipientsPreview.bounced_list.map((r, i) => (
                            <div key={i} className="px-3 py-1.5 flex items-center justify-between">
                              <span className="font-mono text-gray-800 truncate mr-2">{r.email}</span>
                              <span className="text-gray-500 text-[11px] whitespace-nowrap truncate max-w-[240px]" title={r.reason}>{r.reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {recipientsPreview.sample_ok?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-900">Show first {recipientsPreview.sample_ok.length} recipients that will receive</summary>
                        <div className="mt-2 max-h-32 overflow-y-auto font-mono text-gray-700 pl-3 border-l">
                          {recipientsPreview.sample_ok.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>

              {/* Live preview iframe */}
              <div className="border rounded-md overflow-hidden bg-slate-100">
                <div className="bg-[#0A2342] text-white text-xs px-3 py-2 flex items-center justify-between">
                  <span><Eye className="w-4 h-4 inline mr-1" /> Live preview</span>
                  <span className="opacity-60">{dirty ? 'Saved version (unsaved edits not yet rendered)' : 'Saved version'}</span>
                </div>
                <iframe
                  title="Newsletter preview"
                  srcDoc={openDoc.html || '<p style="padding:40px;color:#666;font-family:Helvetica">Preview will appear here after the first save.</p>'}
                  className="w-full bg-white"
                  style={{ minHeight: '70vh', border: 0 }}
                  data-testid="nl-preview-iframe"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Content sources manager */}
      <Dialog open={showSources} onOpenChange={setShowSources}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle><span className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF6B35]" /> Custom RSS sources</span></DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-3">
            We already pull from Tier-1 sources (Scuba News, Divernet, DAN, NOAA etc.) and Google News RSS for SA + Mozambique keywords automatically. Add extras here — any RSS feed URL — and they&apos;ll be folded into next week&apos;s draft.
          </p>
          <div className="grid grid-cols-12 gap-2 mb-2 items-end">
            <div className="col-span-4"><label className="text-xs text-gray-500">Name</label>
              <Input value={newSource.name} onChange={(e) => setNewSource({ ...newSource, name: e.target.value })} placeholder="Marine Megafauna" /></div>
            <div className="col-span-6"><label className="text-xs text-gray-500">RSS URL</label>
              <Input value={newSource.url} onChange={(e) => setNewSource({ ...newSource, url: e.target.value })} placeholder="https://…/feed/" /></div>
            <div className="col-span-2"><Button onClick={addSource} className="bg-[#0A2342] text-white w-full"><Plus className="w-4 h-4" /></Button></div>
          </div>
          <div className="max-h-[40vh] overflow-y-auto border rounded">
            {sources.length === 0 ? (
              <div className="text-sm text-gray-500 p-4 text-center">No custom sources yet.</div>
            ) : sources.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-3 py-2 border-b text-sm last:border-b-0">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#0A2342] truncate">{s.name}</div>
                  <div className="text-xs text-gray-500 truncate">{s.url}</div>
                </div>
                <button onClick={() => deleteSource(s.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSources(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Library Picker */}
      <MediaPicker
        token={token}
        open={mediaOpen}
        onClose={() => { setMediaOpen(false); setPickingForSection(null); }}
        onPick={onMediaPicked}
      />
    </div>
  );
};

export default NewslettersTab;
